// ============================================================
// CallWaveform.js
// Live audio level visualizer for the active call - shows
// talk/silence for BOTH legs (caller and agent) using the
// browser's own WebAudio AnalyserNode on the local mic stream
// and the remote (caller) stream already attached by WebRTCAgent.
//
// Place at: /var/www/ai-server-frontend/ai-chat/src/CallWaveform.js
// ============================================================

import { useEffect, useRef, useState } from 'react';

const SILENCE_THRESHOLD = 8;

function CallWaveform({ localStream, remoteStream, active }) {
  const canvasRef = useRef(null);
  const localAnalyserRef = useRef(null);
  const remoteAnalyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const [localTalking, setLocalTalking] = useState(false);
  const [remoteTalking, setRemoteTalking] = useState(false);

  useEffect(() => {
    if (!active) return;

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;

    const setupAnalyser = (stream) => {
      if (!stream) return null;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      return analyser;
    };

    if (localStream) localAnalyserRef.current = setupAnalyser(localStream);
    if (remoteStream) remoteAnalyserRef.current = setupAnalyser(remoteStream);

    const localData = new Uint8Array(128);
    const remoteData = new Uint8Array(128);

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;

    const historyLen = 120;
    const localHistory = new Array(historyLen).fill(0);
    const remoteHistory = new Array(historyLen).fill(0);

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);

      let lAvg = 0, rAvg = 0;

      if (localAnalyserRef.current) {
        localAnalyserRef.current.getByteFrequencyData(localData);
        lAvg = localData.reduce((a, b) => a + b, 0) / localData.length;
      }
      if (remoteAnalyserRef.current) {
        remoteAnalyserRef.current.getByteFrequencyData(remoteData);
        rAvg = remoteData.reduce((a, b) => a + b, 0) / remoteData.length;
      }

      setLocalTalking(lAvg > SILENCE_THRESHOLD);
      setRemoteTalking(rAvg > SILENCE_THRESHOLD);

      localHistory.push(lAvg);
      localHistory.shift();
      remoteHistory.push(rAvg);
      remoteHistory.shift();

      canvasCtx.clearRect(0, 0, W, H);
      canvasCtx.fillStyle = '#0a0e17';
      canvasCtx.fillRect(0, 0, W, H);

      const midY = H / 2;
      const barW = W / historyLen;

      canvasCtx.fillStyle = 'rgba(68,136,255,0.8)';
      localHistory.forEach((v, i) => {
        const h = Math.min((v / 255) * (H / 2 - 4), H / 2 - 4);
        canvasCtx.fillRect(i * barW, midY - h, barW - 1, h);
      });

      canvasCtx.fillStyle = 'rgba(34,212,122,0.8)';
      remoteHistory.forEach((v, i) => {
        const h = Math.min((v / 255) * (H / 2 - 4), H / 2 - 4);
        canvasCtx.fillRect(i * barW, midY, barW - 1, h);
      });

      canvasCtx.strokeStyle = '#1e2a40';
      canvasCtx.lineWidth = 1;
      canvasCtx.beginPath();
      canvasCtx.moveTo(0, midY);
      canvasCtx.lineTo(W, midY);
      canvasCtx.stroke();
    };

    draw();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    };
  }, [localStream, remoteStream, active]);

  if (!active) {
    return (
      <div style={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a5a7a', fontSize: 11, background: '#0a0e17', borderRadius: 8, border: '1px solid #1e2a40' }}>
        No active call
      </div>
    );
  }

  return (
    <div style={{ background: '#0a0e17', border: '1px solid #1e2a40', borderRadius: 8, overflow: 'hidden' }}>
      <canvas ref={canvasRef} width={560} height={90} style={{ display: 'block', width: '100%', height: 90 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderTop: '1px solid #1e2a40' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#4488ff' }} />
          <span style={{ fontSize: 10, color: '#8899bb' }}>Agent (you)</span>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
            background: localTalking ? '#0a3020' : '#141926',
            color: localTalking ? '#22d47a' : '#4a5a7a',
          }}>
            {localTalking ? 'TALKING' : 'silence'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
            background: remoteTalking ? '#0a3020' : '#141926',
            color: remoteTalking ? '#22d47a' : '#4a5a7a',
          }}>
            {remoteTalking ? 'TALKING' : 'silence'}
          </span>
          <span style={{ fontSize: 10, color: '#8899bb' }}>Caller</span>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#22d47a' }} />
        </div>
      </div>
    </div>
  );
}

export default CallWaveform;
